import { normalizeEndpointsFromForm } from "./gate0Storage";

export const AUTH_TYPE_OPTIONS = ["None", "Bearer Token"];
export const ENDPOINT_METHOD_OPTIONS = ["GET", "POST"];
export const ENDPOINT_PATH_MAX_LENGTH = 120;

const PLACEHOLDER_OPERATION =
  /^(primary api endpoint|endpoint \d+|from fr elicitation(?:\s*\(.*\))?)$/i;

/**
 * Canonical REST endpoint operation (descriptive metadata).
 * Legacy `purpose` / intake `intent` are fallbacks when `operation` is absent.
 * An explicit empty `operation` is not overwritten by purpose.
 */
export function resolveEndpointOperation(ep = {}) {
  const hasOperation =
    ep != null &&
    Object.prototype.hasOwnProperty.call(ep, "operation") &&
    ep.operation != null;
  const raw = String(
    hasOperation ? ep.operation : ep.purpose || ep.intent || ep.description || ""
  ).trim();
  if (!raw || PLACEHOLDER_OPERATION.test(raw)) return "";
  return raw;
}

/**
 * Raw operation text for the Gate 1 Operation input.
 * Do not trim: a trailing space must stay so the user can type the next word.
 * Callers must not use this for save or display of a resolved operation.
 * Falls back to the resolved value only when `operation` is absent.
 */
export function endpointOperationInputValue(ep = {}) {
  const hasOperation =
    ep != null &&
    Object.prototype.hasOwnProperty.call(ep, "operation") &&
    ep.operation != null;
  if (hasOperation) return String(ep.operation);
  return resolveEndpointOperation(ep);
}

export const EMPTY_API_FORM = {
  baseUrl: "",
  authType: "",
  endpoints: [
    {
      id: "ep-1",
      method: "",
      path: "",
      operation: "",
      purpose: "",
      requestBody: "",
      payloads: [],
    },
  ],
};

function filled(value) {
  return Boolean(String(value ?? "").trim());
}

/** True when the user (or prefill) has entered anything in the API surface. */
export function isApiSurfaceInUse(apiForm = {}) {
  if (filled(apiForm.baseUrl) || filled(apiForm.authType)) return true;
  return (apiForm.endpoints || []).some(
    (ep) =>
      filled(ep?.method) ||
      filled(ep?.path) ||
      filled(ep?.operation) ||
      filled(ep?.requestBody)
  );
}

export function methodUsesBody(method) {
  return ["POST", "PUT", "PATCH"].includes(String(method || "").toUpperCase());
}

/** Clamp any stored/parsed method to what the form offers. Empty stays empty in the UI. */
export function normalizeEndpointMethod(method, { allowEmpty = false } = {}) {
  const raw = String(method || "").trim();
  if (!raw) return allowEmpty ? "" : "GET";
  const upper = raw.toUpperCase();
  return ENDPOINT_METHOD_OPTIONS.includes(upper) ? upper : allowEmpty ? "" : "GET";
}

export function normalizeAuthType(authType) {
  return String(authType || "").toLowerCase();
}

export function authTypeNeedsCredentials(authType) {
  const t = normalizeAuthType(authType);
  return Boolean(t) && !t.includes("none");
}

export const EMPTY_CREDENTIALS = {
  bearerToken: "",
  basicEmail: "",
  basicApiToken: "",
  apiKeyHeader: "",
  apiKeyValue: "",
};

export function nextEndpointId(endpoints) {
  const nums = (endpoints || [])
    .map((ep) => parseInt(String(ep.id || "").replace(/\D/g, ""), 10))
    .filter((n) => Number.isFinite(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `ep-${next}`;
}

function endpointRowFromGate0(ep, index) {
  const operation = resolveEndpointOperation(ep);
  const row = {
    id: ep.id || `ep-${index + 1}`,
    method: normalizeEndpointMethod(ep.method, { allowEmpty: true }),
    path: String(ep.path || ""),
    operation,
    purpose: operation,
    requestBody: "",
    payloads: [],
    prompt_field: ep.prompt_field || "",
    response_field: ep.response_field || "",
  };
  if (Array.isArray(ep.payloads) && ep.payloads.length > 0) {
    row.payloads = ep.payloads.map((p, vi) => ({
      name: String(p.name || `variant-${vi + 1}`),
      body:
        typeof p.body === "string"
          ? p.body
          : p.body != null
            ? JSON.stringify(p.body, null, 2)
            : "{}",
      expectStatus:
        p.expect_status != null
          ? String(p.expect_status)
          : p.expectStatus != null
            ? String(p.expectStatus)
            : "",
    }));
  } else if (ep.request_body != null) {
    try {
      row.requestBody =
        typeof ep.request_body === "string"
          ? ep.request_body
          : JSON.stringify(ep.request_body, null, 2);
    } catch {
      row.requestBody = String(ep.request_body);
    }
  } else if (ep.request_body_raw) {
    row.requestBody = String(ep.request_body_raw);
  }
  return row;
}

/** Hydrate editable API form from persisted gate0_data. */
export function apiFormFromGate0Data(gate0 = {}) {
  const endpoints = Array.isArray(gate0.endpoints)
    ? gate0.endpoints.map(endpointRowFromGate0).filter(Boolean)
    : [];
  const rawAuth = String(gate0.auth_type || "").trim();
  return {
    baseUrl: String(gate0.base_url || "").trim(),
    authType: AUTH_TYPE_OPTIONS.includes(rawAuth) ? rawAuth : "",
    endpoints:
      endpoints.length > 0
        ? endpoints
        : JSON.parse(JSON.stringify(EMPTY_API_FORM.endpoints)),
  };
}

function requestSchemaFromBody(body) {
  if (!body || typeof body !== "object" || Array.isArray(body)) return {};
  const schema = {};
  for (const [key, value] of Object.entries(body)) {
    let type = "string";
    if (Array.isArray(value)) type = "array";
    else if (value === null) type = "null";
    else type = typeof value;
    schema[key] = { type, required: true };
  }
  return schema;
}

function previousEndpoint(gate0, ep) {
  const previous = Array.isArray(gate0.endpoints) ? gate0.endpoints : [];
  return (
    previous.find((row) => String(row.id) === String(ep.id)) ||
    previous.find((row) => String(row.path) === String(ep.path)) ||
    null
  );
}

/**
 * Keep a newly typed secret, otherwise the value already saved on the project.
 * A blank field must not wipe a token the user is not looking at.
 */
function keepSavedSecret(nextValue, existingValue) {
  const next = String(nextValue ?? "").trim();
  if (next) return next;
  const existing = String(existingValue ?? "").trim();
  return existing || undefined;
}

function endpointSurfaceKey(ep = {}) {
  const method = String(ep.method || "GET").toUpperCase();
  const raw = String(ep.path || "").trim();
  const path = raw.startsWith("/") ? raw : raw ? `/${raw}` : "";
  let body = ep.request_body ?? null;
  if (body == null && String(ep.requestBody || "").trim()) {
    try {
      body = JSON.parse(String(ep.requestBody).trim());
    } catch {
      body = String(ep.requestBody).trim();
    }
  }
  return `${method} ${path} ${JSON.stringify(body)}`;
}

/**
 * Merge the API form into gate0_data for persistence and probes.
 * When auth is Bearer, api_bearer_token is stored for later metrics calls.
 * A blank token keeps the saved value. Any other auth type deletes it.
 * Callers must not log api_bearer_token or copy it into the execution form.
 * Saved prompt and response paths stay only while method, path, and body are unchanged.
 * A new API must be named by the next probe, not by the previous endpoint's fields.
 */
export function mergeApiFormIntoGate0Data(gate0 = {}, apiForm, credentials = {}) {
  const endpoints = normalizeEndpointsFromForm(apiForm).map((ep) => {
    const prev = previousEndpoint(gate0, ep) || {};
    const keepPaths = Boolean(prev.id || prev.path) && endpointSurfaceKey(prev) === endpointSurfaceKey(ep);
    const row = { ...ep };
    if (!keepPaths) {
      delete row.prompt_field;
      delete row.response_field;
    } else {
      if (prev.prompt_field) row.prompt_field = prev.prompt_field;
      if (prev.response_field) row.response_field = prev.response_field;
    }
    return {
      ...row,
      ...(prev.auth_ref ? { auth_ref: prev.auth_ref } : {}),
      request_schema:
        prev.request_schema && Object.keys(prev.request_schema).length
          ? prev.request_schema
          : requestSchemaFromBody(ep.request_body),
      response_schema: prev.response_schema || {},
      test_data_source: prev.test_data_source || { type: "synthetic" },
      risk_scope: Array.isArray(prev.risk_scope) ? prev.risk_scope : [],
    };
  });
  const authType = String(apiForm.authType || "").trim();
  const previousConfig = gate0.app_config && typeof gate0.app_config === "object" ? gate0.app_config : null;
  const appConfig = previousConfig
    ? {
        ...previousConfig,
        endpoints: (previousConfig.endpoints || []).map((stored) => {
          const match =
            endpoints.find((ep) => String(ep.id) === String(stored.id)) ||
            endpoints.find((ep) => String(ep.path) === String(stored.path));
          if (!match) return stored;
          const next = { ...stored };
          if (match.prompt_field) next.prompt_field = match.prompt_field;
          else delete next.prompt_field;
          if (match.response_field) next.response_field = match.response_field;
          else delete next.response_field;
          return next;
        }),
      }
    : previousConfig;
  const next = {
    ...gate0,
    base_url: String(apiForm.baseUrl || "").trim() || undefined,
    auth_type: authType || undefined,
    rate_limit_rpm: gate0.rate_limit_rpm ?? 60,
    ssl_verify: gate0.ssl_verify !== undefined ? gate0.ssl_verify : true,
    collection: gate0.collection !== undefined ? gate0.collection : null,
    endpoints: endpoints.length ? endpoints : undefined,
    ...(appConfig ? { app_config: appConfig } : {}),
  };
  if (normalizeAuthType(authType).includes("bearer")) {
    const token = keepSavedSecret(credentials.bearerToken, gate0.api_bearer_token);
    if (token) next.api_bearer_token = token;
    else delete next.api_bearer_token;
  } else {
    delete next.api_bearer_token;
  }
  return next;
}

function stripAuthSecrets(appConfig) {
  if (!appConfig || typeof appConfig !== "object") return null;
  const copy = JSON.parse(JSON.stringify(appConfig));
  const auth = copy.auth_configs;
  if (auth && typeof auth === "object") {
    for (const cfg of Object.values(auth)) {
      if (cfg && typeof cfg === "object") {
        delete cfg.token;
        delete cfg.value;
      }
    }
  }
  return copy;
}

function matchReportEndpoint(endpoints, reportEp) {
  const id = String(reportEp.endpoint_id || reportEp.id || "");
  const path = String(reportEp.path || "");
  return (
    endpoints.find((ep) => id && String(ep.id) === id) ||
    endpoints.find((ep) => path && String(ep.path) === path) ||
    null
  );
}

function writeResolvedPaths(target, source) {
  if (!target || !source) return;
  if (source.prompt_field) target.prompt_field = source.prompt_field;
  if (source.response_field) target.response_field = source.response_field;
}

/** Copy resolved paths and a secret-free app_config onto the declaration. */
export function applyProbeFieldsToGate0(gate0 = {}, report = {}) {
  const endpoints = (Array.isArray(gate0.endpoints) ? gate0.endpoints : []).map((ep) => ({ ...ep }));
  const sources = [
    ...(report.endpoints || []),
    ...(report.app_config?.endpoints || []),
  ];
  for (const rep of sources) {
    const target = matchReportEndpoint(endpoints, rep);
    if (!target) continue;
    writeResolvedPaths(target, rep);
    if (!target.request_schema || !Object.keys(target.request_schema).length) {
      target.request_schema = requestSchemaFromBody(target.request_body);
    }
    if (!target.response_schema) target.response_schema = {};
    if (!target.test_data_source) target.test_data_source = { type: "synthetic" };
    if (!Array.isArray(target.risk_scope)) target.risk_scope = [];
  }
  const appConfig = stripAuthSecrets(report.app_config);
  if (appConfig && Array.isArray(appConfig.endpoints)) {
    for (const stored of appConfig.endpoints) {
      const declared = matchReportEndpoint(endpoints, stored);
      writeResolvedPaths(stored, declared);
    }
  }
  return {
    ...gate0,
    rate_limit_rpm: gate0.rate_limit_rpm ?? report.app_config?.rate_limit_rpm ?? 60,
    ssl_verify: gate0.ssl_verify ?? report.app_config?.ssl_verify ?? true,
    collection: gate0.collection !== undefined ? gate0.collection : (report.app_config?.collection ?? null),
    endpoints,
    ...(appConfig ? { app_config: appConfig } : {}),
  };
}

/** Drop the saved API surface so Gate 2 no longer treats it as declared. */
export function clearApiFromGate0Data(gate0 = {}) {
  const next = { ...gate0 };
  delete next.base_url;
  delete next.auth_type;
  delete next.api_bearer_token;
  delete next.endpoints;
  delete next.gate1_readiness_summary;
  delete next.app_config;
  return next;
}
